/**
 * @module pandocService
 * @description Pandoc service for document conversion via the Pandoc server
 *
 * Handles upload and conversion of documents to/from Markdown:
 * - Import: Word (.docx, .doc), ODT, EPUB, HTML, XML/TEI, Markdown (.md) → Pandoc Markdown
 * - Export: Markdown → Word (.docx), ODT
 *
 * Features:
 * - Automatic request timeout with AbortController
 * - Comprehensive file validation (extension + MIME type)
 * - Progress callbacks for upload/conversion tracking
 *
 * Requires the Pandoc server to be running (default: http://localhost:3030)
 *
 * @example
 * import {
 *   checkPandocHealth,
 *   convertDocumentToMarkdown,
 *   exportDocument,
 *   validateFile
 * } from './pandocService';
 *
 * // Check server status
 * const health = await checkPandocHealth();
 * console.log(health.status); // 'ok'
 *
 * // Convert a Word document
 * const result = await convertDocumentToMarkdown(file, {
 *   onProgress: ({ stage, progress }) => console.log(`${stage}: ${progress}%`)
 * });
 * console.log(result.markdown);
 *
 * // Export to Word
 * await exportDocument(markdown, 'document', 'docx');
 */

import { api, files as filesConfig } from '../config';
import { loggers } from './loggerService';
import { getAccessToken } from './supabaseClient';

// Create scoped logger for Pandoc operations
const log = loggers.pandoc;

// ============================================================================
// PROXY MODE SUPPORT
// ============================================================================

/**
 * Check if proxy mode is enabled.
 * When proxy mode is enabled, Pandoc requests go through
 * the Spectral Cloud Portal Edge Function for authentication.
 */
function isProxyModeEnabled(): boolean {
  return api.proxy?.enabled ?? false;
}

/**
 * Get the base URL for Pandoc requests.
 * Returns proxy URL when in proxy mode, direct URL otherwise.
 */
function getPandocBaseUrl(): string {
  if (isProxyModeEnabled() && api.proxy?.supabaseUrl) {
    return `${api.proxy.supabaseUrl}/functions/v1/${api.proxy.pandocProxyFunction}`;
  }
  return api.pandocUrl;
}

/**
 * Execute a Pandoc API request, routing through proxy if enabled.
 *
 * @param path - API path (e.g., '/health', '/convert')
 * @param options - Fetch options
 * @param timeoutMs - Timeout in milliseconds
 * @returns Fetch response
 */
async function pandocFetch(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = api.timeout
): Promise<Response> {
  if (isProxyModeEnabled()) {
    return pandocFetchViaProxy(path, options, timeoutMs);
  }
  return fetchWithTimeout(`${api.pandocUrl}${path}`, options, timeoutMs);
}

/**
 * Execute a Pandoc API request via the Edge Function proxy.
 */
async function pandocFetchViaProxy(
  path: string,
  options: RequestInit = {},
  timeoutMs: number = api.timeout
): Promise<Response> {
  const proxyUrl = `${api.proxy.supabaseUrl}/functions/v1/${api.proxy.pandocProxyFunction}`;
  const accessToken = await getAccessToken();

  if (!accessToken) {
    throw new Error('Not authenticated. Please log in via Spectral Cloud Portal.');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    apikey: api.proxy.supabaseAnonKey,
  };

  const { controller, cleanup } = createTimeoutController(timeoutMs);

  try {
    // Handle different request types
    if (options.body instanceof FormData) {
      // FormData (file upload) - pass path via header
      headers['x-pandoc-path'] = path;

      const response = await fetch(proxyUrl, {
        method: 'POST',
        headers,
        body: options.body,
        signal: controller.signal,
      });
      return response;
    } else if (options.method === 'GET' || !options.method) {
      // GET request - pass path via header
      headers['x-pandoc-path'] = path;
      headers['Content-Type'] = 'application/json';

      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });
      return response;
    } else {
      // POST/PUT with JSON body - include path in body
      headers['Content-Type'] = 'application/json';

      let bodyContent: unknown;
      if (typeof options.body === 'string') {
        bodyContent = JSON.parse(options.body);
      } else {
        bodyContent = options.body;
      }

      const response = await fetch(proxyUrl, {
        method: options.method,
        headers,
        body: JSON.stringify({
          path,
          method: options.method,
          body: bodyContent,
        }),
        signal: controller.signal,
      });
      return response;
    }
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new TimeoutError(`Request to ${path} timed out after ${timeoutMs}ms`);
      }
      if (error.message.includes('fetch') || error.message.includes('network')) {
        throw new NetworkError(`Network error while connecting to proxy: ${error.message}`);
      }
    }
    throw error;
  } finally {
    cleanup();
  }
}

// Log proxy mode status on module load
if (isProxyModeEnabled()) {
  log.info('Pandoc proxy mode enabled - requests will go through Edge Function');
} else {
  log.info('Pandoc direct mode - requests will go to ' + api.pandocUrl);
}

// ============================================================================
// TIMEOUT AND ABORT UTILITIES
// ============================================================================

/** Custom error class for timeout errors */
export class TimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'TimeoutError';
  }
}

/** Custom error class for network errors */
export class NetworkError extends Error {
  constructor(message = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Create an AbortController with automatic timeout
 *
 * @param timeoutMs - Timeout in milliseconds
 * @returns Object containing controller and cleanup function
 */
function createTimeoutController(timeoutMs: number = api.timeout): {
  controller: AbortController;
  timeoutId: ReturnType<typeof setTimeout>;
  cleanup: () => void;
} {
  const controller = new AbortController();

  const timeoutId = setTimeout(() => {
    controller.abort(new TimeoutError(`Request timed out after ${timeoutMs}ms`));
  }, timeoutMs);

  const cleanup = () => {
    clearTimeout(timeoutId);
  };

  return { controller, timeoutId, cleanup };
}

/**
 * Execute fetch with automatic timeout
 *
 * @param url - Request URL
 * @param options - Fetch options
 * @param timeoutMs - Timeout in milliseconds (default: from config)
 * @returns Fetch response
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = api.timeout
): Promise<Response> {
  const { controller, cleanup } = createTimeoutController(timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } catch (error) {
    if (error instanceof Error) {
      // Handle abort errors (timeout)
      if (error.name === 'AbortError' || error instanceof TimeoutError) {
        throw new TimeoutError(`Request to ${url} timed out after ${timeoutMs}ms`);
      }
      // Handle network errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        throw new NetworkError(`Network error while connecting to ${url}: ${error.message}`);
      }
    }
    throw error;
  } finally {
    cleanup();
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Text wrapping mode for conversion */
export type WrapTextMode = 'auto' | 'none' | 'preserve';

/** Progress event stage */
export type ProgressStage = 'uploading' | 'converting' | 'completed';

/** Export format */
export type ExportFormat = 'docx' | 'odt' | 'md' | 'pdf' | 'html';

/** Conversion options */
export interface ConversionOptions {
  /** Extract embedded images (default: true) */
  extractMedia?: boolean;
  /** Preserve heading structure (default: true) */
  preserveHeadings?: boolean;
  /** Text wrapping mode (default: 'auto') */
  wrapText?: WrapTextMode;
  /** Generate standalone document (default: false) */
  standalone?: boolean;
  /** Progress callback */
  onProgress?: (event: ProgressEvent) => void;
}

/** Conversion result */
export interface ConversionResult {
  /** Whether conversion succeeded */
  success: boolean;
  /** Converted Markdown content */
  markdown: string;
  /** Original filename */
  filename: string;
  /** Original file size in bytes */
  size: number;
  /** ISO 8601 conversion timestamp */
  convertedAt: string;
}

/** Progress event during conversion */
export interface ProgressEvent {
  /** Current stage */
  stage: ProgressStage;
  /** Progress percentage (0-100) */
  progress: number;
  /** Current file (batch only) */
  current?: number;
  /** Total files (batch only) */
  total?: number;
}

/** Heading in document */
export interface DocumentHeading {
  /** Heading level (1-6) */
  level: number;
  /** Heading text */
  text: string;
}

/** Document metadata extracted from Markdown */
export interface DocumentMetadata {
  /** Document title (first H1) */
  title: string;
  /** Author (if found) */
  author: string;
  /** Date (if found) */
  date: string;
  /** All headings */
  headings: DocumentHeading[];
  /** Word count */
  wordCount: number;
  /** Whether document contains images */
  hasImages: boolean;
  /** Whether document contains tables */
  hasTables: boolean;
}

/** File validation result */
export interface FileValidation {
  /** Whether file is valid for conversion */
  valid: boolean;
  /** Validation errors */
  errors: string[];
  /** Validation warnings */
  warnings: string[];
}

/** Health check response from Pandoc server */
export interface HealthResponse {
  /** Server status */
  status: string;
  /** Pandoc version */
  pandocVersion: string;
}

/** Batch conversion result */
export interface BatchConversionResult {
  /** Successful conversions */
  results: ConversionResult[];
  /** Errors for failed conversions */
  errors: Array<{
    filename: string;
    error: string;
  }>;
}

/** Chapter for EPUB export */
export interface EpubChapter {
  /** Filename (used for ordering) */
  filename: string;
  /** Markdown content */
  content: string;
}

/** Image for EPUB export */
export interface EpubImage {
  /** Filename used in markdown references */
  filename: string;
  /** MIME type (image/png, image/jpeg, etc.) */
  mimeType: string;
  /** Base64-encoded image data (without data URL prefix) */
  data: string;
}

/** Metadata for EPUB export */
export interface EpubMetadata {
  /** Book title */
  title?: string;
  /** Book author */
  author?: string;
  /** Custom YAML metadata block (will be merged with title/author) */
  yaml?: string;
  /** Cover image as base64 data URL */
  coverImage?: string;
}

/** Template for EPUB export (CSS or fonts) */
export interface EpubTemplate {
  /** Filename of the template */
  filename: string;
  /** Template type: css, font, or other */
  templateType: 'css' | 'font' | 'other';
  /** MIME type */
  mimeType: string;
  /** Content (text for CSS, base64 for fonts) */
  content: string;
}

/** Lua filter for EPUB export */
export interface EpubLuaFilter {
  /** Filename of the Lua filter */
  filename: string;
  /** Lua script content */
  content: string;
}

/** Server conversion response */
interface ServerConversionResponse {
  markdown: string;
  filename: string;
  size: number;
  convertedAt: string;
}

/** Server error response */
interface ServerErrorResponse {
  message?: string;
  error?: string;
}

// ============================================================================
// CONSTANTS (kept for backward compatibility, use pandocFetch instead)
// ============================================================================

/**
 * Pandoc server URL (from central config)
 * @deprecated Use pandocFetch() which handles proxy mode automatically
 */
const PANDOC_API_URL: string = api.pandocUrl;

// ============================================================================
// PUBLIC FUNCTIONS
// ============================================================================

/**
 * Check Pandoc server status and availability
 *
 * @returns Server health info
 * @throws Error if server is not accessible
 *
 * @example
 * try {
 *   const health = await checkPandocHealth();
 *   console.log(`Pandoc ${health.pandocVersion} is running`);
 * } catch (error) {
 *   console.error('Pandoc server unavailable:', error.message);
 * }
 */
export async function checkPandocHealth(): Promise<HealthResponse> {
  try {
    // Use short timeout for health check (5 seconds)
    const response = await pandocFetch('/health', {}, 5000);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json() as HealthResponse;
  } catch (error) {
    if (error instanceof TimeoutError) {
      throw new Error('Pandoc server is not responding (timeout)');
    }
    if (error instanceof NetworkError) {
      throw new Error(`Pandoc server is not accessible: ${error.message}`);
    }
    log.error('Error checking Pandoc server', { error });
    throw new Error(`Pandoc server is not accessible: ${(error as Error).message}`);
  }
}

/**
 * Convert a document file to Markdown
 *
 * Supported formats: .docx, .doc, .epub, .pdf
 * Maximum file size: 50MB
 *
 * @param file - File to convert
 * @param options - Conversion options
 * @returns Conversion result with markdown content
 * @throws Error if file is invalid, too large, or conversion fails
 *
 * @example
 * const input = document.querySelector('input[type="file"]');
 * const file = input.files[0];
 *
 * const result = await convertDocumentToMarkdown(file, {
 *   extractMedia: true,
 *   onProgress: ({ stage, progress }) => {
 *     progressBar.style.width = `${progress}%`;
 *     statusText.textContent = stage;
 *   }
 * });
 *
 * editor.setContent(result.markdown);
 */
export async function convertDocumentToMarkdown(
  file: File,
  options: ConversionOptions = {}
): Promise<ConversionResult> {
  const {
    extractMedia = true,
    preserveHeadings = true,
    wrapText = 'auto',
    standalone = false,
    onProgress = null
  } = options;

  try {
    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!filesConfig.allowedExtensions.includes(fileExtension)) {
      throw new Error(`Unsupported format: ${fileExtension}. Accepted formats: ${filesConfig.allowedExtensions.join(', ')}`);
    }

    // Check MIME type (warning only, not blocking - MIME types can be unreliable)
    if (file.type && !filesConfig.allowedMimeTypes.includes(file.type)) {
      log.warn('Unexpected MIME type, proceeding based on extension', { mimeType: file.type, filename: file.name });
    }

    // Check size
    if (file.size > filesConfig.maxSize) {
      throw new Error(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Maximum: ${filesConfig.maxSize / (1024 * 1024)}MB`);
    }

    if (onProgress) onProgress({ stage: 'uploading', progress: 0 });

    // Prepare FormData
    const formData = new FormData();
    formData.append('file', file);
    formData.append('extractMedia', extractMedia.toString());
    formData.append('preserveHeadings', preserveHeadings.toString());
    formData.append('wrapText', wrapText);
    formData.append('standalone', standalone.toString());

    if (onProgress) onProgress({ stage: 'converting', progress: 50 });

    // Send to Pandoc server with timeout (longer for conversion - 60 seconds)
    const response = await pandocFetch(
      '/convert',
      {
        method: 'POST',
        body: formData
      },
      60000 // 60 seconds for conversion
    );

    if (!response.ok) {
      const error = await response.json() as ServerErrorResponse;
      throw new Error(error.message || `Conversion failed: ${response.statusText}`);
    }

    const result = await response.json() as ServerConversionResponse;

    if (onProgress) onProgress({ stage: 'completed', progress: 100 });

    return {
      success: true,
      markdown: result.markdown,
      filename: result.filename,
      size: result.size,
      convertedAt: result.convertedAt
    };

  } catch (error) {
    if (error instanceof TimeoutError) {
      log.error('Conversion timed out', { filename: file.name, error });
      throw new Error('Document conversion timed out. The file may be too large or complex.');
    }
    if (error instanceof NetworkError) {
      log.error('Network error during conversion', { filename: file.name, error });
      throw new Error(`Could not connect to conversion server: ${error.message}`);
    }
    log.error('Error during conversion', { filename: file.name, error });
    throw error;
  }
}

/**
 * Convert multiple documents in a single batch request
 *
 * Maximum 10 files per batch. Each file must be a valid format.
 *
 * @param files - List of files to convert
 * @param options - Conversion options
 * @returns Batch results
 * @throws Error if no files provided, too many files, or batch fails
 *
 * @example
 * const results = await convertMultipleDocuments(fileList, {
 *   onProgress: ({ current, total, progress }) => {
 *     console.log(`Converting ${current}/${total} (${progress}%)`);
 *   }
 * });
 *
 * for (const result of results.results) {
 *   await db.documents.add({ title: result.filename, content: result.markdown });
 * }
 */
export async function convertMultipleDocuments(
  files: FileList | File[],
  options: ConversionOptions = {}
): Promise<BatchConversionResult> {
  const { onProgress = null } = options;

  try {
    if (!files || files.length === 0) {
      throw new Error('No files provided');
    }

    if (files.length > filesConfig.maxBatchFiles) {
      throw new Error(`Maximum ${filesConfig.maxBatchFiles} files per batch`);
    }

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    formData.append('extractMedia', options.extractMedia?.toString() || 'true');
    formData.append('preserveHeadings', options.preserveHeadings?.toString() || 'true');

    if (onProgress) onProgress({ stage: 'uploading', progress: 0, current: 0, total: files.length });

    // Longer timeout for batch conversion (2 minutes)
    const response = await pandocFetch(
      '/convert/batch',
      {
        method: 'POST',
        body: formData
      },
      120000 // 2 minutes for batch conversion
    );

    if (!response.ok) {
      const error = await response.json() as ServerErrorResponse;
      throw new Error(error.message || 'Batch conversion failed');
    }

    const result = await response.json() as BatchConversionResult;

    if (onProgress) onProgress({ stage: 'completed', progress: 100, current: files.length, total: files.length });

    return result;

  } catch (error) {
    if (error instanceof TimeoutError) {
      log.error('Batch conversion timed out', { fileCount: files.length, error });
      throw new Error('Batch conversion timed out. Try converting fewer files at once.');
    }
    if (error instanceof NetworkError) {
      log.error('Network error during batch conversion', { fileCount: files.length, error });
      throw new Error(`Could not connect to conversion server: ${error.message}`);
    }
    log.error('Error during batch conversion', { fileCount: files.length, error });
    throw error;
  }
}

/**
 * Extract metadata from converted Markdown content
 *
 * Extracts document structure information for indexing and display.
 *
 * @param markdown - Markdown content (from conversion)
 * @returns Extracted metadata
 *
 * @example
 * const result = await convertDocumentToMarkdown(file);
 * const meta = extractDocumentMetadata(result.markdown);
 *
 * console.log(`Title: ${meta.title}`);
 * console.log(`${meta.wordCount} words`);
 * console.log(`${meta.headings.length} sections`);
 */
export function extractDocumentMetadata(markdown: string): DocumentMetadata {
  const metadata: DocumentMetadata = {
    title: '',
    author: '',
    date: '',
    headings: [],
    wordCount: 0,
    hasImages: false,
    hasTables: false
  };

  // Extract title (first H1), stripping Pandoc attribute blocks {#id .class key=value}
  const titleMatch = markdown.match(/^#\s+(.+)$/m);
  if (titleMatch) {
    metadata.title = titleMatch[1].replace(/\s*\{[^}]+\}\s*$/, '').trim();
  }

  // Extract headings
  const headingMatches = markdown.matchAll(/^(#{1,6})\s+(.+)$/gm);
  for (const match of headingMatches) {
    metadata.headings.push({
      level: match[1].length,
      text: match[2].trim()
    });
  }

  // Count words
  const words = markdown.replace(/[#*_`[\]()]/g, '').split(/\s+/).filter(w => w.length > 0);
  metadata.wordCount = words.length;

  // Check for images
  metadata.hasImages = /!\[.*?\]\(.*?\)/.test(markdown);

  // Check for tables
  metadata.hasTables = /\|.*\|/.test(markdown);

  return metadata;
}

/**
 * Format a filename for use as a document slug
 *
 * Transforms the filename into a URL-safe format:
 * - Removes file extension
 * - Converts to lowercase
 * - Removes accents
 * - Replaces spaces with hyphens
 * - Removes special characters
 *
 * @param originalName - Original filename
 * @returns Formatted filename with .md extension
 *
 * @example
 * formatDocumentName('My Document.docx');   // 'my-document.md'
 * formatDocumentName('Café Résumé.pdf');    // 'cafe-resume.md'
 */
export function formatDocumentName(originalName: string): string {
  // Remove extension
  let name = originalName.replace(/\.(docx?|epub|pdf)$/i, '');

  // Clean and format
  name = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9\s-]/g, '') // Keep only letters, numbers, spaces, dashes
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Replace multiple dashes with single
    .replace(/^-|-$/g, ''); // Remove dashes at start/end

  return name + '.md';
}

/**
 * Validate a file before attempting conversion
 *
 * Checks:
 * - File is provided
 * - Extension is supported (.docx, .doc, .epub, .pdf)
 * - File size is under 50MB (warning if > 10MB)
 * - Filename is reasonable length
 *
 * @param file - File to validate
 * @returns Validation result with errors and warnings
 *
 * @example
 * const { valid, errors, warnings } = validateFile(file);
 *
 * if (!valid) {
 *   alert('Cannot convert: ' + errors.join(', '));
 *   return;
 * }
 *
 * if (warnings.length > 0) {
 *   console.warn('Warnings:', warnings);
 * }
 *
 * await convertDocumentToMarkdown(file);
 */
export function validateFile(file: File | null): FileValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!file) {
    errors.push('No file selected');
    return { valid: false, errors, warnings };
  }

  // Check extension
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

  if (!filesConfig.allowedExtensions.includes(fileExtension)) {
    errors.push(`Unsupported format: ${fileExtension}`);
  }

  // Check size
  if (file.size > filesConfig.maxSize) {
    errors.push(`File too large: ${(file.size / 1024 / 1024).toFixed(2)}MB (max: ${filesConfig.maxSize / (1024 * 1024)}MB)`);
  }

  if (file.size > filesConfig.warningSizeThreshold) {
    warnings.push('Large file, conversion may take time');
  }

  // Check name
  if (file.name.length > filesConfig.maxFilenameLength) {
    warnings.push('Very long filename');
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/** Chapter for multi-document export */
export interface ExportChapter {
  /** Filename (used for ordering) */
  filename: string;
  /** Markdown content */
  content: string;
}

/** Options for document export with citation support */
export interface ExportOptions {
  /** Bibliography items in CSL JSON format */
  bibliography?: unknown[];
  /** CSL style content (XML string) */
  csl?: string;
  /** Multiple chapters (uses --file-scope for isolated footnotes) */
  chapters?: ExportChapter[];
}

/**
 * Export a Markdown document to another format and trigger download
 *
 * For 'md' format, exports directly without server.
 * For 'docx' and 'odt', uses Pandoc server for conversion.
 *
 * @param markdown - Markdown content to export (ignored if options.chapters is provided)
 * @param filename - Base filename (without extension)
 * @param format - Output format (default: 'docx')
 * @param options - Optional export options (bibliography, CSL style, chapters)
 * @returns Promise that resolves when download is triggered
 * @throws Error if server unavailable or conversion fails
 *
 * @example
 * // Export single document to Word
 * await exportDocument(content, 'my-novel', 'docx');
 *
 * // Export with citations
 * await exportDocument(content, 'my-paper', 'pdf', {
 *   bibliography: cslItems,
 *   csl: cslStyleContent
 * });
 *
 * // Export multiple chapters with isolated footnotes (--file-scope)
 * await exportDocument('', 'my-book', 'docx', {
 *   chapters: [
 *     { filename: '01-intro.md', content: '# Introduction\n...' },
 *     { filename: '02-chapter.md', content: '# Chapter 1\n...' }
 *   ]
 * });
 */
export async function exportDocument(
  markdown: string,
  filename: string,
  format: ExportFormat = 'docx',
  options: ExportOptions = {}
): Promise<void> {
  const { bibliography, csl, chapters } = options;

  // All formats go through Pandoc server
  // For markdown: converts <span class="..."> back to [text]{.class}
  // For other formats: Pandoc handles the conversion
  let response: Response;
  try {
    // Build request body
    const requestBody: Record<string, unknown> = {
      filename,
      format
    };

    // Use chapters array if provided (enables --file-scope for isolated footnotes)
    if (chapters && chapters.length > 0) {
      requestBody.chapters = chapters;
      log.info('Exporting with chapters (--file-scope)', { chapterCount: chapters.length });
    } else {
      requestBody.markdown = markdown;
    }

    if (bibliography && bibliography.length > 0 && csl) {
      requestBody.bibliography = bibliography;
      requestBody.csl = csl;
      log.info('Exporting with citeproc', { bibliographyCount: bibliography.length });
    }

    response = await pandocFetch(
      '/export',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      },
      120000 // 2 minutes for multi-chapter export
    );
  } catch (error) {
    if (error instanceof TimeoutError) {
      log.error('Export timed out', { filename, format, error });
      throw new Error('Document export timed out. The document may be too large.');
    }
    if (error instanceof NetworkError) {
      log.error('Network error during export', { filename, format, error });
      throw new Error(`Unable to contact Pandoc server. Verify it is running at ${PANDOC_API_URL}`);
    }
    log.error('Error during export', { filename, format, error });
    throw new Error(`Unable to contact Pandoc server. Verify it is running at ${PANDOC_API_URL}`);
  }

  if (!response.ok) {
    // Try to read error as JSON, otherwise as text
    let errorMessage: string;
    try {
      const errorData = await response.json() as ServerErrorResponse;
      errorMessage = errorData.message || errorData.error || response.statusText;
    } catch {
      errorMessage = await response.text() || response.statusText;
    }
    throw new Error(`Export failed: ${errorMessage}`);
  }

  // Check response Content-Type
  const contentType = response.headers.get('Content-Type');
  if (contentType && contentType.includes('application/json')) {
    // It's a JSON error
    const errorData = await response.json() as ServerErrorResponse;
    throw new Error(errorData.message || errorData.error || 'Unknown error');
  }

  // Get blob and trigger download
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.replace(/\.[^.]+$/, '') + '.' + format;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export multiple documents to EPUB format
 *
 * Sends multiple markdown chapters to the Pandoc server for EPUB generation.
 * Chapters should be provided in the desired order (typically alphabetical by filename).
 *
 * @param chapters - Array of chapters with filename and content
 * @param metadata - Optional metadata (title, author, yaml, coverImage)
 * @param images - Optional array of images referenced in chapters
 * @param templates - Optional array of templates (CSS, fonts) for EPUB styling
 * @returns Promise that resolves when download is triggered
 * @throws Error if server unavailable or conversion fails
 *
 * @example
 * const chapters = [
 *   { filename: '00-preface.md', content: '# Preface\n\nThis is the preface...' },
 *   { filename: '01-chapter-1.md', content: '# Chapter 1\n\n![Hero](hero.jpg)\n\nOnce upon a time...' }
 * ];
 * const images = [
 *   { filename: 'hero.jpg', mimeType: 'image/jpeg', data: 'base64encodeddata...' }
 * ];
 * const templates = [
 *   { filename: 'style.css', templateType: 'css', mimeType: 'text/css', content: 'body { font-family: serif; }' }
 * ];
 * await exportToEpub(chapters, { title: 'My Book', author: 'Author Name' }, images, templates);
 */
export async function exportToEpub(
  chapters: EpubChapter[],
  metadata: EpubMetadata = {},
  images: EpubImage[] = [],
  templates: EpubTemplate[] = [],
  luaFilters: EpubLuaFilter[] = [],
  options: { epubTitlePage?: boolean; epubVersion?: 'epub2' | 'epub3'; lang?: string; navReorder?: boolean; toc?: boolean; tocDepth?: number; tocTitle?: string } = {}
): Promise<void> {
  const { title, author, yaml, coverImage } = metadata;

  let response: Response;
  try {
    // Build request body
    const requestBody: Record<string, unknown> = {
      chapters,
      title,
      author,
      yaml,
      coverImage,
      images,
      templates
    };

    // Add Lua filters if any (server will apply them with --lua-filter=)
    if (luaFilters.length > 0) {
      requestBody.luaFilters = luaFilters;
      log.info('Including Lua filters in EPUB export', {
        count: luaFilters.length,
        filenames: luaFilters.map(f => f.filename)
      });
    }

    // Add --epub-title-page=false when custom titlepage document exists
    if (options.epubTitlePage === false) {
      requestBody.epubTitlePage = false;
      log.info('Disabling Pandoc title page (custom titlepage document detected)');
    }

    // TOC options (--toc, --toc-depth, --metadata=toc-title)
    if (options.toc !== undefined) {
      requestBody.toc = options.toc;
    }
    if (options.tocDepth !== undefined) {
      requestBody.tocDepth = options.tocDepth;
    }
    if (options.tocTitle) {
      requestBody.tocTitle = options.tocTitle;
    }

    // Set EPUB version (epub2 or epub3, defaults to epub3 server-side)
    if (options.epubVersion) {
      requestBody.epubVersion = options.epubVersion;
      log.info(`EPUB version: ${options.epubVersion}`);
    }

    // Set language for post-processing (cover SVG title, dc:language)
    if (options.lang) {
      requestBody.lang = options.lang;
    }

    // Nav reorder: move nav.xhtml/toc.ncx after copyright-page or titlepage in spine
    if (options.navReorder !== undefined) {
      requestBody.navReorder = options.navReorder;
      log.info(`Nav reorder: ${options.navReorder}`);
    }

    // EPUB generation can take longer, use 2 minute timeout
    response = await pandocFetch(
      '/export/epub',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      },
      120000 // 2 minutes for EPUB generation
    );
  } catch (error) {
    if (error instanceof TimeoutError) {
      log.error('EPUB export timed out', { chapterCount: chapters.length, title, error });
      throw new Error('EPUB export timed out. The book may have too many chapters or be too large.');
    }
    if (error instanceof NetworkError) {
      log.error('Network error during EPUB export', { chapterCount: chapters.length, title, error });
      throw new Error(`Unable to contact Pandoc server. Verify it is running at ${PANDOC_API_URL}`);
    }
    log.error('Error during EPUB export', { chapterCount: chapters.length, title, error });
    throw new Error(`Unable to contact Pandoc server. Verify it is running at ${PANDOC_API_URL}`);
  }

  if (!response.ok) {
    // Read response as text first, then try to parse as JSON
    let errorMessage: string;
    const responseText = await response.text();
    try {
      const errorData = JSON.parse(responseText) as ServerErrorResponse;
      errorMessage = errorData.message || errorData.error || response.statusText;
    } catch {
      errorMessage = responseText || response.statusText;
    }
    throw new Error(`EPUB export failed: ${errorMessage}`);
  }

  // Get blob and trigger download (post-processing is handled server-side)
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = title ? `${title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-')}.epub` : 'book.epub';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  checkPandocHealth,
  convertDocumentToMarkdown,
  convertMultipleDocuments,
  extractDocumentMetadata,
  formatDocumentName,
  validateFile,
  exportDocument,
  exportToEpub,
  TimeoutError,
  NetworkError
};
